import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

interface IProps {
    value: number;
    setValue: (newValue: number) => void;
    hasParkingDetails: boolean | null;
    hasDevelopmentDetails: boolean | null;
    hasTravelPlan: boolean | null;
    isAdmin: boolean;
    parkingSupported: boolean;
    isEditing: boolean;
    addDevelopment: () => void;
    addParking: () => void;
    addTravelPlan: () => void;
}

export default function SitePageOptions({
    value,
    setValue,
    hasParkingDetails,
    hasDevelopmentDetails,
    hasTravelPlan,
    isAdmin,
    parkingSupported,
    isEditing,
    addDevelopment,
    addParking,
    addTravelPlan,
}: IProps) {

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        const unavailable =
            (newValue === 3 && !hasDevelopmentDetails) ||
            (newValue === 4 && !hasParkingDetails) ||
            ((newValue === 6 || newValue === 7) && !hasTravelPlan);

        if (unavailable) return;
        setValue(newValue);
    };

    const missingLabel = (label: string, add: () => void, showAdd: boolean) => (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {label}
            {showAdd && (
                <Tooltip title={`Add ${label}`}>
                    <IconButton
                        component="span"
                        size="small"
                        color="primary"
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            add();
                        }}
                        aria-label={`Add ${label}`}
                    >
                        <AddCircleOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );

    return (
        <Tabs value={value} onChange={handleChange}>
            <Tab label="Location Details" sx={{ color: 'black' }} />
            <Tab label="Public Transport" sx={{ color: 'black' }} />
            <Tab label="Design Features for Non-car modes" wrapped sx={{ maxWidth: '200px', fontSize: '0.875rem', color: 'black' }} />
            <Tab
                disabled={!hasDevelopmentDetails && (!isAdmin || !isEditing)}
                aria-disabled={!hasDevelopmentDetails}
                label={missingLabel('Development Units', addDevelopment, isAdmin && isEditing && !hasDevelopmentDetails)}
                sx={{ color: hasDevelopmentDetails ? 'black' : 'text.disabled' }}
            />
            <Tab
                disabled={!hasParkingDetails && (!isAdmin || !isEditing || !parkingSupported)}
                aria-disabled={!hasParkingDetails}
                label={missingLabel('Parking', addParking, isAdmin && isEditing && parkingSupported && !hasParkingDetails)}
                sx={{ color: hasParkingDetails ? 'black' : 'text.disabled' }}
            />
            <Tab label="Survey Days" sx={{ color: 'black' }} />
            <Tab
                disabled={!hasTravelPlan && (!isAdmin || !isEditing)}
                aria-disabled={!hasTravelPlan}
                label={missingLabel('Travel Plan', addTravelPlan, isAdmin && isEditing && !hasTravelPlan)}
                sx={{ color: hasTravelPlan ? 'black' : 'text.disabled' }}
            />
            <Tab
                disabled={!hasTravelPlan && (!isAdmin || !isEditing)}
                aria-disabled={!hasTravelPlan}
                label={missingLabel('Travel Plan Monitoring Report', addTravelPlan, isAdmin && isEditing && !hasTravelPlan)}
                sx={{ color: hasTravelPlan ? 'black' : 'text.disabled' }}
            />
        </Tabs>
    );
}
